import {TIMELINE_COLOR_MAP} from '../../../shared/constants';
import {formatMonth} from '../../../utils/formatters';
import {CustomMenuItem} from '../arr-menu/ArrMenu';
import {SelectedBarInfoType} from './ArrSelectableColumnChart';

export type TimelineType = 'yearly' | 'monthly' | 'daily';

export interface TimelineApiItem {
  period: string;
  documentCount: number;
  documentVolume?: number;
}

export interface TimelineApiResponse {
  yearlyData: TimelineApiItem[];
  monthlyData: TimelineApiItem[];
  dailyData: TimelineApiItem[];
}

export interface BarColumnsChartSelectableSeries {
  label: string;
  data: number[];
  color: string;
}

export const getBarActualValueFromIndex = (
  data: TimelineApiItem[],
  selectedBar: Map<string, SelectedBarInfoType>,
  type: TimelineType
) => {
  const payload: {[key: string]: number}[] = [];

  switch (type) {
    case 'yearly':
      Array.from(selectedBar.values()).forEach((bar) => {
        if (bar.barIndex !== undefined)
          payload.push({
            Year: new Date(data[bar.barIndex].period).getFullYear(),
          });
      });
      break;
    case 'monthly':
      {
        Array.from(selectedBar.values()).forEach((bar) => {
          if (bar.barIndex !== undefined) {
            payload.push({
              Year: new Date(data[bar.barIndex].period).getFullYear(),
              Month: new Date(data[bar.barIndex].period).getMonth() + 1,
            });
          }
        });
      }
      break;
    case 'daily':
      {
        Array.from(selectedBar.values()).forEach((bar) => {
          if (bar.barIndex !== undefined) {
            const date = new Date(data[bar.barIndex].period);
            payload.push({
              Year: date.getFullYear(),
              Month: date.getMonth() + 1,
              Day: date.getDate(),
            });
          }
        });
      }
      break;
    default:
      break;
  }
  return payload;
};

export const totalDocumentToView = (
  selectedBar: Map<string, SelectedBarInfoType>
) => {
  let selectedDocument = 0;
  Array.from(selectedBar.values()).forEach((bar) => {
    selectedDocument += bar.value;
  });
  return selectedDocument;
};

export const convertApiToChart = (
  data: TimelineApiItem[],
  type: TimelineType
): {labels: string[]; series: BarColumnsChartSelectableSeries[]} => {
  let labels: string[] = [];

  const formatLabel = (value: string | string[]) =>
    Array.isArray(value) ? value.join(' ') : value;

  switch (type) {
    case 'yearly':
      labels = data.map((d) =>
        formatLabel(new Date(d.period).getFullYear().toString())
      );
      break;
    case 'monthly':
      labels = data.map((d) => formatLabel(formatMonth(d.period)));
      break;
    case 'daily':
      labels = data.map((d) =>
        formatLabel(new Date(d.period).getDate().toString())
      );
      break;
  }

  const series: BarColumnsChartSelectableSeries[] = [
    {
      label: 'Documents',
      data: data.map((d) => d.documentCount),
      color: TIMELINE_COLOR_MAP.DefaultSeries,
    },
  ];

  return {labels, series};
};

export const getMockTimelineData = (): TimelineApiResponse => ({
  yearlyData: [
    {period: '2021-01-01T00:00:00.000Z', documentCount: 5},
    {period: '2022-01-01T00:00:00.000Z', documentCount: 2},
    {period: '2023-01-01T00:00:00.000Z', documentCount: 7},
    {period: '2024-01-01T00:00:00.000Z', documentCount: 4},
    {period: '2025-01-01T00:00:00.000Z', documentCount: 6},
  ],
  monthlyData: [
    {period: '2025-01-01T00:00:00.000Z', documentCount: 1},
    {period: '2025-02-01T00:00:00.000Z', documentCount: 4},
    {period: '2025-03-01T00:00:00.000Z', documentCount: 3},
    {period: '2025-04-01T00:00:00.000Z', documentCount: 2},
    {period: '2025-05-01T00:00:00.000Z', documentCount: 6},
  ],
  dailyData: [
    {period: '2025-11-01T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-02T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-03T00:00:00.000Z', documentCount: 1},
    {period: '2025-11-04T00:00:00.000Z', documentCount: 4},
    {period: '2025-11-05T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-06T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-07T00:00:00.000Z', documentCount: 5},
    {period: '2025-11-08T00:00:00.000Z', documentCount: 1},
    {period: '2025-11-09T00:00:00.000Z', documentCount: 4},
    {period: '2025-11-10T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-11T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-12T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-13T00:00:00.000Z', documentCount: 1},
    {period: '2025-11-14T00:00:00.000Z', documentCount: 4},
    {period: '2025-11-15T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-16T00:00:00.000Z', documentCount: 5},
    {period: '2025-11-17T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-18T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-19T00:00:00.000Z', documentCount: 1},
    {period: '2025-11-20T00:00:00.000Z', documentCount: 4},
    {period: '2025-11-21T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-22T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-23T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-24T00:00:00.000Z', documentCount: 5},
    {period: '2025-11-25T00:00:00.000Z', documentCount: 1},
    {period: '2025-11-26T00:00:00.000Z', documentCount: 4},
    {period: '2025-11-27T00:00:00.000Z', documentCount: 2},
    {period: '2025-11-28T00:00:00.000Z', documentCount: 3},
    {period: '2025-11-29T00:00:00.000Z', documentCount: 1},
    {period: '2025-11-30T00:00:00.000Z', documentCount: 4},
  ],
});
export const getDefaultActionMenuItems = (): CustomMenuItem[] => [
  {id: '1', label: 'Bulk Tag'},
  {id: '2', label: 'Bulk Folder'},
  {id: '3', label: 'Bulk Assign'},
  {id: '4', label: 'Bulk Release'},
  {
    id: '5',
    label: 'View',
    submenu: [
      {id: 'view1', label: 'View in Doc View'},
      {id: 'view2', label: 'View in Doc View in New tab'},
      {id: 'view3', label: 'View in Doc List'},
    ],
  },
];
